import * as React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {Card, CardContent, CardHeader, Grid} from '@mui/material';
import {PlaylistAddCheck} from '@mui/icons-material';
import {useAppDispatch} from '../store';
import {useAppTranslation} from '../services/i18n';
import {selectAuthUser} from 'store/selectors';
import {getFilteredTotal, isApiResultError, type PagedResult} from '../helpers/api';
import {dateToGui, toCalendarDate} from '../helpers/date';
import {fetchUserApprovalRequests} from 'store/approvals';
import {ApprovalRequestInfo, toApprovalRequestInfo} from 'model/approval';
import {
    colFilterAny,
    colNoSort,
    createCol,
    DataGrid,
    DataGridCol,
    DataGridColFilter,
    DataGridItemAction,
    DataGridMode,
    DataGridState,
    FilterContext,
    ItemsState,
    OrderDirType
} from 'components/DataGrid';
import {TextFormField} from 'components/form/TextFormField';
import {ApprovalValidityBadge} from 'components/approval/ApprovalValidityBadge';
import {ApprovalFilterDialog} from 'components/approval/ApprovalFilterDialog';
import type {ApprovalFilterType, ApprovalGridFilter} from 'pages/ApprovalsPage';

/**
 * All `Users::ApprovalRequests::List` sorts a completion by -- `subject_date` is
 * not among them, even though it is the list's own default order, and asking for
 * it explicitly is refused with VALIDATION_FAILED.
 */
const API_SORT_COLUMNS: (keyof ApprovalRequestInfo)[] = ['subject_title', 'subject_expiration_date'];

/**
 * The grid reuses the management filter model and dialog, but the only column
 * filter that makes sense for one's own records is the provider.
 */
const defaultState: DataGridState<ApprovalRequestInfo, ApprovalGridFilter> = {
    // describes what the API returns by default; not sent, see API_SORT_COLUMNS
    orderCol: 'subject_date',
    orderDir: OrderDirType.DESC,
    filter: {
        search: '',
        provider: '',
    },
    pageSize: 25,
    page: 1
};

export const StudentApprovalsPage = () => {
    const t = useAppTranslation();
    const dispatch = useAppDispatch();
    const authUser = useSelector(selectAuthUser);

    const [dataGridState, setDataGridState] = useState(defaultState);
    const [itemsState, setItemsState] = useState<ItemsState<ApprovalRequestInfo>>({items: [], isLoading: true});
    const [colFilter, setColFilter] = useState<DataGridColFilter<ApprovalGridFilter, ApprovalFilterType>>();

    const {filter, orderCol, orderDir, page, pageSize} = dataGridState;

    const queryArgs = useMemo(() => ({
        filterFulltext: filter.search || undefined,
        filterProviders: filter.provider ? [filter.provider] : undefined,
        sort: orderCol && API_SORT_COLUMNS.includes(orderCol)
            ? String(orderCol) + ':' + (orderDir || 'asc') : undefined,
    }), [filter, orderCol, orderDir]);

    const handleFetch = useCallback(async () => {
        if (!authUser?.id) {
            return;
        }
        setItemsState(s => ({...s, isLoading: true}));
        const res = await dispatch(fetchUserApprovalRequests({
            ...queryArgs,
            userId: authUser.id,
            page,
            perPage: pageSize
        }));
        if (isApiResultError(res)) {
            setItemsState(s => ({...s, isLoading: false}));
            return;
        }
        const result = res.payload as PagedResult<any>;
        const items = (result.items || []).map(toApprovalRequestInfo);
        setItemsState(s => ({
            items: page && page > 1 ? [...(s.items || []), ...items] : items,
            total: getFilteredTotal(result),
            isLoading: false
        }));
    }, [queryArgs, page, pageSize, authUser?.id, dispatch]);

    useEffect(() => {
        handleFetch().then();
    }, [handleFetch]);

    const handleOpenDetail = useCallback((item: ApprovalRequestInfo) => {
        window.location.href = '#/dashboard/approvals/' + item.id;
    }, []);

    const actions: DataGridItemAction<ApprovalRequestInfo>[] = useMemo(() => [
        // read-only for the student: no edit, no approval actions
        {title: 'approvals.action.detail', callback: handleOpenDetail},
    ], [handleOpenDetail]);

    const cols = useMemo(() => {
        const filterContext: FilterContext<ApprovalGridFilter, ApprovalFilterType> = {
            defaultFilter: defaultState.filter,
            currentFilter: dataGridState.filter,
            onOpen: setColFilter,
            onApply: (values) => {
                setDataGridState(s => ({...s, filter: {...s.filter, ...values}, page: 1}));
            }
        };
        const providerFilter: DataGridColFilter<ApprovalGridFilter, ApprovalFilterType> = {
            ...filterContext,
            filterName: 'provider',
            isApplied: () => !!dataGridState.filter.provider
        };

        // no price and no status here -- the student sees neither
        const cols: DataGridCol<ApprovalRequestInfo>[] = [
            // not the management list's "název / téma": a thematic request never reaches this screen
            createCol(t('approvals.field.subjectTitle'), 'subject_title', (v) => v || '-', '30%'),
            // subject_provider is not in the API's sort whitelist, only its filter
            colFilterAny(
                colNoSort(createCol(t('approvals.table.provider'), 'subject_provider', (v) => v || '-', '25%', 'sm')),
                providerFilter),
            // the term is the list's default order but not in its sort whitelist
            colNoSort(createCol(t('approvals.table.subjectDate'), 'subject_date',
                (v) => dateToGui(toCalendarDate(v)) || '-', '20%', 'sm')),
            // the virtual column exists so the header can sort by the key the API knows;
            // the value itself lives on the certificate association
            createCol(t('approvals.table.expiration'), 'subject_expiration_date',
                (v) => v
                    ? <span className={'tw-flex tw-items-center tw-gap-8 tw-flex-wrap'}>
                        {dateToGui(toCalendarDate(v))}
                        <ApprovalValidityBadge expiration={v}/>
                    </span>
                    : '-', '20%'),
        ];
        return cols;
    }, [t, dataGridState.filter]);

    const createFilter = useCallback((formProps: any) => <Grid container spacing={2}>
        <Grid size={{lg: 4, md: 6, xs: 12}}>
            <TextFormField name={'search'} type={'search'}
                label={t('approvals.filter.search')}
                onBlur={formProps.submitForm}
                maxlength={100}/>
        </Grid>
    </Grid>, [t]);

    return <div className={'layout-fill layout-column tw-absolute'}>
        <div className={'kws-content-margin flex tw-h-100'}>
            <Card className={'tw-h-full tw-flex tw-flex-col'}>
                <CardHeader
                    className={'max-sm:tw-flex-wrap max-sm:tw-gap-12'}
                    title={<>
                        <PlaylistAddCheck/>
                        <span>{t('approvals.student.title')}</span>
                    </>}
                />
                <CardContent className={'tw-p-32 tw-pb-0 tw-flex-grow tw-flex tw-flex-col tw-overflow-auto'}>
                    <DataGrid
                        cols={cols}
                        state={dataGridState}
                        defaultState={defaultState}
                        setState={setDataGridState}
                        itemsState={itemsState}
                        mode={DataGridMode.SERVER}
                        createFilter={createFilter}
                        actions={actions}
                        isActionMenu
                        rowClick={handleOpenDetail}
                        emptyListMessage={t('approvals.student.emptyList')}
                        emptySearchMessage={t('approvals.emptySearch')}
                    />
                    {!!colFilter && <ApprovalFilterDialog colFilter={colFilter} onClose={() => setColFilter(undefined)}/>}
                </CardContent>
            </Card>
        </div>
    </div>;
};
